import { API_ROUTES, createQueryKey, useApiQuery } from '@shared/api-utils';
import { useClientConfig } from '@shared/utils';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { OfferBundlePromotion } from '../../types/types';

type UseOffersDataParams = {
  isLoggedIn: boolean;
  filteredVertical?: string[];
};

type UseOffersDataReturn = {
  availableQuery: UseQueryResult<OfferBundlePromotion[], Error>;
  previousQuery: UseQueryResult<OfferBundlePromotion[], Error>;
};

function normaliseOffers(data: OfferBundlePromotion[] | null): OfferBundlePromotion[] {
  return data ?? [];
}

/**
 * Custom hook to fetch available and previous offers data.
 * - Available offers are fetched from the private API if the user is logged in, otherwise from the public API.
 * - When `filteredVertical` is set, the offers-and-rewards request includes `tags` query params.
 * - Previous offers are only fetched when the user is logged in.
 *
 * @param {boolean} params.isLoggedIn - Whether the user is logged in.
 * @param {string[]} params.filteredVertical - Optional verticals sent as `tags` on the offers-and-rewards API call.
 * @returns {UseOffersDataReturn} Object containing query results for available and previous offers.
 */
function useOffersData({ isLoggedIn, filteredVertical = [] }: UseOffersDataParams): UseOffersDataReturn {
  const { apiLocale } = useClientConfig();
  const availableRoute = (isLoggedIn ? API_ROUTES.PROMOTIONS.OFFERS_AND_REWARDS.PRIVATE : API_ROUTES.PROMOTIONS.OFFERS_AND_REWARDS.PUBLIC).replace(':locale', apiLocale);
  const availableQuery = useApiQuery<OfferBundlePromotion[], OfferBundlePromotion[]>(availableRoute, {
    credentials: true,
    ...(filteredVertical.length > 0 ? { params: { tags: filteredVertical.map(vertical => vertical.toUpperCase()).join(',') } } : {}),
    queryOptions: { staleTime: 0, select: normaliseOffers },
  });

  const previousRoute = API_ROUTES.PROMOTIONS.OFFER_BUNDLES_NON_ACTIVE.replace(':status', 'previous');
  const previousQuery = useApiQuery<OfferBundlePromotion[], OfferBundlePromotion[]>(previousRoute, {
    credentials: true,
    queryOptions: {
      enabled: isLoggedIn,
      staleTime: 0,
      select: normaliseOffers,
    },
  });

  // Setting up a dummy query for previous offers when the user is not logged in for ease of use with query boundaries
  const dummyQuery = useQuery<OfferBundlePromotion[]>({
    queryKey: ['dummy', ...createQueryKey(previousRoute)],
    queryFn: async () => [],
  });

  return {
    availableQuery,
    previousQuery: isLoggedIn ? previousQuery : dummyQuery,
  };
}

export { useOffersData };
export type { UseOffersDataParams, UseOffersDataReturn };
